export const SITE = {
  BASE_URL: (import.meta.env.VITE_SITE_URL as string | undefined)?.trim() ?? "",
  NAME: "웨이플러스",
  LOCALE: "ko_KR",
  DEFAULT_TITLE: "웨이플러스",
  DEFAULT_DESCRIPTION: "웨이플러스는 AI와 데이터 기술을 기반으로 스마트 관광과 DX 서비스를 제공하는 기업입니다."
} as const;

export interface DocumentMetaInput {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  robots?: string;
}

export interface ApplyDocumentMetaOptions {
  // false면 og:*/twitter:*/canonical을 주입하지 않고 기존 태그도 제거 (admin 등 내부 페이지용)
  social?: boolean;
}

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function removeMeta(selector: string): void {
  document.head.querySelector(selector)?.remove();
}

function upsertLink(selector: string, rel: string, href: string): void {
  let tag = document.head.querySelector<HTMLLinkElement>(selector);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function removeLink(selector: string): void {
  document.head.querySelector(selector)?.remove();
}

const SOCIAL_META_SELECTORS = [
  'meta[property="og:type"]',
  'meta[property="og:site_name"]',
  'meta[property="og:locale"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:image"]',
  'meta[property="og:url"]',
  'meta[name="twitter:card"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]'
];

export function applyDocumentMeta(meta: DocumentMetaInput, path?: string, options?: ApplyDocumentMetaOptions): void {
  if (typeof document === "undefined") return;

  const social = options?.social ?? true;

  const rawTitle = meta.title?.trim() || SITE.DEFAULT_TITLE;
  const title =
    rawTitle === SITE.NAME || rawTitle.endsWith(` | ${SITE.NAME}`) ? rawTitle : `${rawTitle} | ${SITE.NAME}`;
  const description = meta.description?.trim() || SITE.DEFAULT_DESCRIPTION;
  const robots = meta.robots ?? "index,follow";

  document.title = title;

  upsertMeta('meta[name="description"]', "name", "description", description);
  upsertMeta('meta[name="robots"]', "name", "robots", robots);

  if (!social) {
    // admin 등 내부 페이지: 소셜/SEO 공유용 태그 모두 제거
    SOCIAL_META_SELECTORS.forEach(removeMeta);
    removeLink('link[rel="canonical"]');
    return;
  }

  const ogTitle = meta.ogTitle?.trim() || title;
  const ogDescription = meta.ogDescription?.trim() || description;

  upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
  upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE.NAME);
  upsertMeta('meta[property="og:locale"]', "property", "og:locale", SITE.LOCALE);
  upsertMeta('meta[property="og:title"]', "property", "og:title", ogTitle);
  upsertMeta('meta[property="og:description"]', "property", "og:description", ogDescription);
  upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", ogTitle);
  upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", ogDescription);

  if (meta.ogImage) {
    upsertMeta('meta[property="og:image"]', "property", "og:image", meta.ogImage);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", meta.ogImage);
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
  } else {
    removeMeta('meta[property="og:image"]');
    removeMeta('meta[name="twitter:image"]');
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary");
  }

  // og:url과 canonical은 쿼리/해시를 제외한 canonical URL로 일관되게 주입
  const origin = SITE.BASE_URL || (typeof window !== "undefined" ? window.location.origin : "");
  if (origin && path) {
    const canonicalUrl = `${origin}${path}`;
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    // canonical은 공식 도메인이 확정된 경우에만 주입 (잘못된 canonical은 SEO에 해롭다)
    if (SITE.BASE_URL) {
      upsertLink('link[rel="canonical"]', "canonical", canonicalUrl);
    } else {
      removeLink('link[rel="canonical"]');
    }
  }
}
